import { useEffect, useState } from 'react';
import { api } from '../../../lib/api';
import { CreateProductDto, Product } from '../../../types/products';

export function useUpdateProduct(id: string, onSuccess?: () => void) {
  const [form, setForm] = useState<CreateProductDto>({ sku: '', name: '', price: 0, imageUrl: '', description: '' });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: Product }>(`/products/${id}`);
      const p = res.data;
      setForm({
        sku: p.sku || '',
        name: p.name,
        price: p.price,
        imageUrl: p.imageUrl || '',
        description: p.description || '',
      });
      setRemoveImage(false);
      setImageFile(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setField = <K extends keyof CreateProductDto>(key: K, value: CreateProductDto[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const submit = async () => {
    setLoading(true);
    if (form.price <= 0) {
      setError('Price must be greater than 0');
      setLoading(false);
      return;
    }
    try {
      const fd = new FormData();
      if (form.sku && form.sku.trim() !== '') fd.append('sku', form.sku.trim());
      fd.append('name', form.name);
      fd.append('price', String(Number(form.price)));
      if (form.description) fd.append('description', form.description);
      if (imageFile) fd.append('image', imageFile);
      if (removeImage) fd.append('imageUrl', ''); // signal clear
      await api.put<{ data: Product }>(`/products/${id}`, fd);
      onSuccess?.();
    } catch (e: any) {
      setError(e.message || 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  return { form, setField, submit, loading, error, setImageFile, imageFile, removeImage, setRemoveImage };
}
